import {
  CalendarClock,
  FileSpreadsheet,
  FileText,
  Gauge,
  HardHat,
  MapPin,
  OctagonAlert,
  ScanEye,
  ShieldCheck,
  Sparkles,
  Upload,
} from "lucide-react";
import { NFR_TARGETS } from "@taskiq/shared";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/Feedback";
import { IconList } from "@/components/ui/IconList";
import { PageHeader } from "@/components/ui/PageHeader";

/**
 * Placeholder for compliance reporting, and the same shape as `Assessments` on
 * purpose: badge, band, empty state, two cards of things that are already true.
 *
 * ── Why it exists before it does anything ───────────────────────────────────
 *
 * Reporting is the reason a customer buys this. The whole pitch is that an
 * assessment is timestamped, geotagged and pushed into the compliance system
 * they already answer to, and a console with no Reports entry reads as a product
 * that captures evidence and then loses interest in it. `report.export` is
 * already a capability in `rbac.ts` and already withheld from a field officer,
 * so the rail entry is gated correctly from the first day rather than being
 * retrofitted behind a role check later.
 *
 * ── Nothing on this page is invented ────────────────────────────────────────
 *
 * There is no fake chart and no sample PDF. What a report will contain is the
 * record set the data model already names, and the ladder and PPE numbers are
 * read from `NFR_TARGETS` rather than typed - the same rule `SecondScale`
 * follows, and for the same reason: a hard-coded 75.5 on a page like this keeps
 * claiming 75.5 long after the product has moved.
 *
 * What it is waiting on is not a design. It is the assessment tables, and behind
 * those the two decisions in `docs/open-questions.md` - the AI provider, and
 * on-device inference for PPE. An export of records that do not exist is not a
 * thing that can be built early.
 */
export function Reports() {
  return (
    <div className="page">
      <PageHeader
        title="Reports"
        badge={<Badge tone="accent">Coming soon</Badge>}
        description="Compliance evidence, packaged for the people who ask for it."
      />

      <Card>
        <EmptyState
          icon={<FileText />}
          title="No reports yet"
          description="Once assessments start arriving, this is where you'll build and schedule them - by location, by crew or by date range, scoped to the locations you can reach."
        />
      </Card>

      <div className="preview-grid">
        <Card>
          <CardHeader
            title="What a report will cover"
            subtitle="Every record a completed pre-task assessment leaves behind"
          />
          <CardBody>
            <IconList
              layout="stack"
              items={[
                {
                  icon: ScanEye,
                  text: "Hazards raised by the area scan, with the control logged against each one",
                },
                {
                  icon: OctagonAlert,
                  text: "Stop-work events: who raised one, and who resolved it",
                },
                {
                  icon: HardHat,
                  text: `PPE verdicts, and ladder angles against the 4:1 rule (${NFR_TARGETS.ladderTargetAngleDeg}° ± ${NFR_TARGETS.ladderAngleToleranceDeg}°)`,
                },
                {
                  icon: MapPin,
                  text: "The timestamp and geotag on each assessment, as captured on the device",
                },
                {
                  icon: Gauge,
                  text: `Time taken, against the ${NFR_TARGETS.assessmentSeconds}-second target`,
                },
              ]}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="How they get out of here"
            subtitle="Three routes, because three different people ask for this"
          />
          <CardBody>
            <IconList
              layout="stack"
              items={[
                {
                  icon: FileSpreadsheet,
                  text: "Downloaded on demand, for a supervisor answering a question today",
                },
                {
                  icon: CalendarClock,
                  text: "Emailed on a schedule, bucketed by the location's timezone rather than the reader's",
                },
                {
                  icon: Upload,
                  text: "Pushed into the customer's own compliance system, which is the point of the product",
                },
              ]}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
